import { ProductPurchaseOption, ProductPurchaseType } from '../productMap';
import { CurrencySymbol } from 'components/shared/CurrencySymbol';
import { ReactElement } from 'react';

type M1Data = {
  taxInclusive: boolean;
  plans: {
    [key in ProductPurchaseOption]?: {
      [key in ProductPurchaseType]?: M1ProductData;
    };
  };
};

export type M1ProductData = {
  displayName?: string;
  price: number;
  strikedPrice?: number;
  variantId: string;
  internalPid?: ProductPurchaseType;
  image?: HTMLImageElement;
  secondaryImage?: string;
  title?: string;
  subtitle1?: string;
  subtitle2?: string;
  checkoutUrl?: string;
  planId?: number;
  tag?: ReactElement;
};

export type NewM1Data = {
  [key in ProductPurchaseOption]?: {
    title: string;
    defaultProduct: ProductPurchaseType;
    directCheckout: boolean;
    discountCode?: string;
    outOfStock?: boolean;
    plans: { [key in ProductPurchaseType]?: M1ProductData };
  };
};

export const newM1Data: NewM1Data =
  process.env['NEXT_PUBLIC_ENVIRONMENT'] === 'PRODUCTION'
    ? {
        [ProductPurchaseOption.x]: {
          title: 'Special Prices',
          defaultProduct: ProductPurchaseType.otp_24_week,
          directCheckout: false,
          plans: {
            [ProductPurchaseType.otp_2_week]: {
              displayName:
                'Ultrahuman M1 | 2 Weeks Trial continuous glucose monitoring',
              price: 7_499,
              variantId: 'gid://shopify/ProductVariant/40566813556806',
              internalPid: ProductPurchaseType.otp_2_week,
              secondaryImage: '',
              title: '2+2 Weeks',
              subtitle1: '2 CGM Sensors',
              subtitle2: 'Only with UltrahumanX',
              tag: <>save {CurrencySymbol.inr}7,499</>,
            },
            [ProductPurchaseType.otp_12_week]: {
              displayName:
                'Ultrahuman M1 | 12 Weeks continuous glucose monitoring',
              price: 34_999,
              variantId: 'gid://shopify/ProductVariant/40596792016966',
              internalPid: ProductPurchaseType.otp_12_week,
              secondaryImage: '',
              title: '12+6 Weeks',
              subtitle1: '9 CGM Sensors',
              subtitle2: 'Only with UltrahumanX',
              tag: <>save {CurrencySymbol.inr}32,492</>,
            },
            [ProductPurchaseType.otp_24_week]: {
              displayName:
                'Ultrahuman M1 | 24 Weeks continuous glucose monitoring',
              price: 64_999,
              variantId: 'gid://shopify/ProductVariant/40566761324614',
              internalPid: ProductPurchaseType.otp_24_week,
              secondaryImage: '',
              title: '24+12 Weeks',
              subtitle1: '18 CGM Sensors',
              subtitle2: 'Only with UltrahumanX',
              tag: <>save {CurrencySymbol.inr}69,983</>,
            },
            [ProductPurchaseType.otp_1_year]: {
              displayName:
                'Ultrahuman M1 | 52 Weeks continuous glucose monitoring',
              price: 1_34_999,
              variantId: 'gid://shopify/ProductVariant/40566763487302',
              internalPid: ProductPurchaseType.otp_1_year,
              secondaryImage: '',
              title: '52+12 Weeks',
              subtitle1: '32 CGM Sensors',
              subtitle2: 'Only with UltrahumanX',
              tag: <>save {CurrencySymbol.inr}1,04,969</>,
            },
          },
        },
        [ProductPurchaseOption.oneTimePurchase]: {
          title: 'One Time Purchase',
          defaultProduct: ProductPurchaseType.otp_24_week,
          directCheckout: false,
          discountCode: 'BFSM120',
          plans: {
            [ProductPurchaseType.otp_2_week]: {
              displayName:
                'Ultrahuman M1 | 2 Weeks Trial continuous glucose monitoring',
              price: 4_699,
              variantId: 'gid://shopify/ProductVariant/45142433988678',
              internalPid: ProductPurchaseType.otp_2_week,
              secondaryImage: '',
              title: '2 Weeks Trial',
              subtitle1: '1 CGM Sensor',
              subtitle2: 'Delivery on purchase',
            },
            [ProductPurchaseType.otp_4_week]: {
              displayName:
                'Ultrahuman M1 | 4 Weeks continuous glucose monitoring',
              price: 8_999,
              variantId: 'gid://shopify/ProductVariant/46462776082502',
              internalPid: ProductPurchaseType.otp_4_week,
              secondaryImage: '',
              title: '4 Weeks',
              subtitle1: '2 CGM Sensors',
              subtitle2: 'Delivery on purchase',
              tag: <>save {CurrencySymbol.inr}399</>,
            },
            [ProductPurchaseType.otp_12_week]: {
              displayName:
                'Ultrahuman M1 | 12 Weeks continuous glucose monitoring',
              price: 25_999,
              variantId: 'gid://shopify/ProductVariant/45142434021446',
              internalPid: ProductPurchaseType.otp_12_week,
              secondaryImage: '',
              title: '12 Weeks',
              subtitle1: '6 CGM Sensors',
              subtitle2: 'Delivery on purchase',
              tag: <>save {CurrencySymbol.inr}2,195</>,
            },
            [ProductPurchaseType.otp_24_week]: {
              displayName:
                'Ultrahuman M1 | 24 Weeks continuous glucose monitoring',
              price: 50_999,
              variantId: 'gid://shopify/ProductVariant/45142434054214',
              internalPid: ProductPurchaseType.otp_24_week,
              secondaryImage: '',
              title: '24 Weeks',
              subtitle1: '12 CGM Sensors',
              subtitle2: 'Delivery on demand',
              tag: <>save {CurrencySymbol.inr}5,389</>,
            },
            [ProductPurchaseType.otp_1_year]: {
              displayName:
                'Ultrahuman M1 | 52 Weeks continuous glucose monitoring',
              price: 1_08_999,
              variantId: 'gid://shopify/ProductVariant/45142434086982',
              internalPid: ProductPurchaseType.otp_1_year,
              secondaryImage: '',
              title: '52 Weeks',
              subtitle1: '26 CGM Sensors',
              subtitle2: 'Delivery on demand',
              tag: <>save {CurrencySymbol.inr}13,175</>,
            },
          },
        },
        [ProductPurchaseOption.membership]: {
          title: 'Subscription',
          defaultProduct: ProductPurchaseType.membership_1_sensor_month,
          directCheckout: false,
          discountCode: '',
          plans: {
            [ProductPurchaseType.membership_1_sensor_month]: {
              title: '2 Weeks Monitoring/Month',
              price: 4_169,
              subtitle1:
                "Every month we'll ship one sensor that lasts two weeks",
              subtitle2: 'Delivery Monthly',
              internalPid: ProductPurchaseType.membership_1_sensor_month,
              variantId: 'gid://shopify/ProductVariant/40459179655238',
              planId: 11112979,
            },
            [ProductPurchaseType.membership_2_sensor_month]: {
              title: 'Full Month Monitoring',
              price: 8_199,
              subtitle1: "Every month we'll ship two sensors that last a month",
              subtitle2: 'Delivery Monthly',
              internalPid: ProductPurchaseType.membership_2_sensor_month,
              variantId: 'gid://shopify/ProductVariant/40459180769350',
              planId: 11112980,
            },
          },
        },
      }
    : {
        [ProductPurchaseOption.x]: {
          title: 'Special Prices',
          defaultProduct: ProductPurchaseType.otp_24_week,
          directCheckout: false,
          plans: {
            [ProductPurchaseType.otp_2_week]: {
              displayName:
                'Ultrahuman M1 | 2 Weeks Trial continuous glucose monitoring',
              price: 7_499,
              variantId: 'gid://shopify/ProductVariant/40234818895945',
              internalPid: ProductPurchaseType.otp_2_week,
              secondaryImage: '',
              title: '2+2 Weeks',
              subtitle1: '2 CGM Sensors',
              subtitle2: 'Delivery on purchase',
              tag: <>save {CurrencySymbol.inr}7,499</>,
            },
            [ProductPurchaseType.otp_12_week]: {
              displayName:
                'Ultrahuman M1 | 12 Weeks continuous glucose monitoring',
              price: 34_999,
              variantId: 'gid://shopify/ProductVariant/40234818895945',
              internalPid: ProductPurchaseType.otp_12_week,
              secondaryImage: '',
              title: '12+6 Weeks',
              subtitle1: '9 CGM Sensors',
              subtitle2: 'Delivery on purchase',
              tag: <>save {CurrencySymbol.inr}32,492</>,
            },
            [ProductPurchaseType.otp_24_week]: {
              displayName:
                'Ultrahuman M1 | 24 Weeks continuous glucose monitoring',
              price: 64_999,
              variantId: 'gid://shopify/ProductVariant/40234818895945',
              internalPid: ProductPurchaseType.otp_24_week,
              secondaryImage: '',
              title: '24+12 Weeks',
              subtitle1: '18 CGM Sensors',
              subtitle2: 'Delivery on demand',
              tag: <>save {CurrencySymbol.inr}69,983</>,
            },
            [ProductPurchaseType.otp_1_year]: {
              displayName:
                'Ultrahuman M1 | 52 Weeks continuous glucose monitoring',
              price: 1_34_999,
              variantId: 'gid://shopify/ProductVariant/40234818895945',
              internalPid: ProductPurchaseType.otp_1_year,
              secondaryImage: '',
              title: '52+12 Weeks',
              subtitle1: '32 CGM Sensors',
              subtitle2: 'Delivery on demand',
              tag: <>save {CurrencySymbol.inr}1,04,969</>,
            },
          },
        },
        [ProductPurchaseOption.oneTimePurchase]: {
          title: 'One Time Purchase',
          defaultProduct: ProductPurchaseType.otp_24_week,
          directCheckout: false,
          discountCode: 'BFSM120',
          plans: {
            [ProductPurchaseType.otp_2_week]: {
              displayName:
                'Ultrahuman M1 | 2 Weeks Trial continuous glucose monitoring',
              price: 3_759,
              strikedPrice: 4_699,
              variantId: 'gid://shopify/ProductVariant/40234818895945',
              internalPid: ProductPurchaseType.otp_2_week,
              secondaryImage: '',
              title: '2 Weeks Trial',
              subtitle1: '1 CGM Sensor',
              subtitle2: 'Delivery on purchase',
              tag: <>save {CurrencySymbol.inr}940</>,
            },
            [ProductPurchaseType.otp_4_week]: {
              displayName:
                'Ultrahuman M1 | 4 Weeks continuous glucose monitoring',
              price: 7_199,
              strikedPrice: 8_999,
              variantId: 'gid://shopify/ProductVariant/40234818895945',
              internalPid: ProductPurchaseType.otp_4_week,
              secondaryImage: '',
              title: '4 Weeks',
              subtitle1: '2 CGM Sensors',
              subtitle2: 'Delivery on purchase',
              tag: <>save {CurrencySymbol.inr}1,800</>,
            },
            [ProductPurchaseType.otp_12_week]: {
              displayName:
                'Ultrahuman M1 | 12 Weeks continuous glucose monitoring',
              price: 20_799,
              strikedPrice: 25_999,
              variantId: 'gid://shopify/ProductVariant/40234818895945',
              internalPid: ProductPurchaseType.otp_12_week,
              secondaryImage: '',
              title: '12 Weeks',
              subtitle1: '6 CGM Sensors',
              subtitle2: 'Delivery on purchase',
              tag: <>save {CurrencySymbol.inr}5,200</>,
            },
            [ProductPurchaseType.otp_24_week]: {
              displayName:
                'Ultrahuman M1 | 24 Weeks continuous glucose monitoring',
              price: 40_799,
              strikedPrice: 50_999,
              variantId: 'gid://shopify/ProductVariant/40234818895945',
              internalPid: ProductPurchaseType.otp_24_week,
              secondaryImage: '',
              title: '24 Weeks',
              subtitle1: '12 CGM Sensors',
              subtitle2: 'Delivery on demand',
              tag: <>save {CurrencySymbol.inr}10,200</>,
            },
            [ProductPurchaseType.otp_1_year]: {
              displayName:
                'Ultrahuman M1 | 52 Weeks continuous glucose monitoring',
              price: 87_199,
              strikedPrice: 1_08_999,
              variantId: 'gid://shopify/ProductVariant/40234818895945',
              internalPid: ProductPurchaseType.otp_1_year,
              secondaryImage: '',
              title: '52 Weeks',
              subtitle1: '26 CGM Sensors',
              subtitle2: 'Delivery on demand',
              tag: <>save {CurrencySymbol.inr}21,800</>,
            },
          },
        },
      };

export const m1data: M1Data = {
  taxInclusive: true,
  plans: {
    [ProductPurchaseOption.oneTimePurchase]: {
      'uh-m1-two-week': {
        displayName:
          'Ultrahuman M1 | 2 Weeks Trial continuous glucose monitoring',
        price: 4_699,
        variantId: 'gid://shopify/ProductVariant/45142433988678',
        secondaryImage: '',
        title: '2 Weeks Trial',
        subtitle1: '1 CGM Sensor',
        subtitle2: 'Delivery on purchase',
      },
      'uh-m1-four-week': {
        displayName: 'Ultrahuman M1 | 4 Weeks continuous glucose monitoring',
        price: 8_999,
        variantId: 'gid://shopify/ProductVariant/46462776082502',
        secondaryImage: '',
        title: '4 Weeks',
        subtitle1: '2 CGM Sensors',
        subtitle2: 'Delivery on purchase',
      },
      'uh-m1-twelve-week': {
        displayName: 'Ultrahuman M1 | 12 Weeks continuous glucose monitoring',
        price: 25_999,
        variantId: 'gid://shopify/ProductVariant/45142434021446',
        secondaryImage: '',
        title: '12 Weeks',
        subtitle1: '6 CGM Sensors',
        subtitle2: 'Delivery on purchase',
      },
      'uh-m1-twenty-four-week': {
        displayName: 'Ultrahuman M1 | 24 Weeks continuous glucose monitoring',
        price: 50_999,
        variantId: 'gid://shopify/ProductVariant/45142434054214',
        secondaryImage: '',
        title: '24 Weeks',
        subtitle1: '12 CGM Sensors',
        subtitle2: 'Delivery on demand',
      },
      'uh-m1-year': {
        displayName: 'Ultrahuman M1 | 52 Weeks continuous glucose monitoring',
        price: 1_08_999,
        variantId: 'gid://shopify/ProductVariant/45142434086982',
        secondaryImage: '',
        title: '52 Weeks',
        subtitle1: '26 CGM Sensors',
        subtitle2: 'Delivery on demand',
      },
    },
    [ProductPurchaseOption.x]: {
      'uh-m1-two-week-x': {
        displayName:
          'Ultrahuman M1 x | 2 Weeks Trial continuous glucose monitoring',
        price: 7_499,
        variantId: 'gid://shopify/ProductVariant/40458091036742',
        secondaryImage: '',
        title: '2 Weeks Trial x',
        subtitle1: '2 CGM Sensors',
        subtitle2: 'Only with UltrahumanX',
      },
      'uh-m1-twelve-week-x': {
        displayName: 'Ultrahuman M1 | 12 Weeks continuous glucose monitoring',
        price: 34_999,
        variantId: 'gid://shopify/ProductVariant/40458093953094',
        secondaryImage: '',
        title: '12 Weeks x',
        subtitle1: '9 CGM Sensors',
        subtitle2: 'Only with UltrahumanX',
      },
      'uh-m1-twenty-four-week-x': {
        displayName: 'Ultrahuman M1 | 24 Weeks continuous glucose monitoring',
        price: 64_999,
        variantId: 'gid://shopify/ProductVariant/40458095722566',
        secondaryImage: '',
        title: '24 Weeks x',
        subtitle1: '18 CGM Sensors',
        subtitle2: 'Only with UltrahumanX',
      },
      'uh-m1-year-x': {
        displayName: 'Ultrahuman M1 | 52 Weeks continuous glucose monitoring',
        price: 1_34_999,
        variantId: 'gid://shopify/ProductVariant/40458096541766',
        secondaryImage: '',
        title: '52 Weeks x',
        subtitle1: '32 CGM Sensors',
        subtitle2: 'Only with UltrahumanX',
      },
    },
  },
};
